import type { MantineColorsTuple } from '@mantine/core';

// Палитра Mantine строится из ССЫЛОК var(--*) на src/styles/_tokens.scss, а не из hex —
// _tokens.scss остаётся единственным источником правды, а public/custom.css (per-config
// Русагро/Акрон) перекрашивает Mantine автоматически.
//
// Рампы в _tokens.scss неоднородны (7/8/11 ступеней, разный индекс -main). Нормализуем
// к 10 ступеням Mantine (idx 0 = светлый ... 9 = тёмный) по канону:
// «все *-main → idx5, primaryShade: 5» — тогда один primaryShade консистентно работает
// для color="primary|error|success|warning|attention|...".

// 8-ступенчатые, main=60 → idx5 (primary/error/purple/magenta/cyan/teal): 10..80, верх дублируем
const ramp8 = (n: string): MantineColorsTuple => [
    `var(--${n}-10)`,
    `var(--${n}-20)`,
    `var(--${n}-30)`,
    `var(--${n}-40)`,
    `var(--${n}-50)`,
    `var(--${n}-60-main)`,
    `var(--${n}-70)`,
    `var(--${n}-80)`,
    `var(--${n}-80)`,
    `var(--${n}-80)`,
];

// 7-ступенчатые, main=50 → idx5 (success/warning): idx4 дублирует 40, верх дублирует 70
const ramp7 = (n: string): MantineColorsTuple => [
    `var(--${n}-10)`,
    `var(--${n}-20)`,
    `var(--${n}-30)`,
    `var(--${n}-40)`,
    `var(--${n}-40)`,
    `var(--${n}-50-main)`,
    `var(--${n}-60)`,
    `var(--${n}-70)`,
    `var(--${n}-70)`,
    `var(--${n}-70)`,
];

// attention: 8 ступеней, main=50 → idx5 (есть 80)
const attentionRamp: MantineColorsTuple = [
    'var(--attention-10)',
    'var(--attention-20)',
    'var(--attention-30)',
    'var(--attention-40)',
    'var(--attention-40)',
    'var(--attention-50-main)',
    'var(--attention-60)',
    'var(--attention-70)',
    'var(--attention-80)',
    'var(--attention-80)',
];

// neutral: 11 ступеней (есть 00/05, нет -main) → 10: схлопываем 00 (#fff уходит в theme.white)
const neutralRamp: MantineColorsTuple = [
    'var(--neutral-05)',
    'var(--neutral-10)',
    'var(--neutral-20)',
    'var(--neutral-30)',
    'var(--neutral-40)',
    'var(--neutral-50)',
    'var(--neutral-60)',
    'var(--neutral-70)',
    'var(--neutral-80)',
    'var(--neutral-90)',
];

// dark: семейство, из которого Mantine в тёмной схеме берёт поверхности, текст и границы
// (--mantine-color-body/text/default*/placeholder… и [data-mantine-color-scheme='dark']-правила
// своих компонентов). Без него поверх нашей палитры ляжет дефолтная серая тема Mantine
// (#242424 / #c9c9c9). Роли ступеней — как у Mantine (0 — текст, 4 — границы, 6 — заливка
// инпута, 7 — подложка), значения — наша нейтральная рампа, которая в тёмном блоке
// _tokens.scss уже инвертирована: neutral-90 там светлый текст, neutral-00 — тёмная карточка.
// Дальше 7 у нас темнее карточки ничего нет, поэтому 8/9 повторяют 7.
const darkRamp: MantineColorsTuple = [
    'var(--neutral-90)', // 0: основной текст
    'var(--neutral-80)', // 1
    'var(--neutral-50)', // 2: dimmed
    'var(--neutral-40)', // 3: placeholder, disabled-текст, шевроны
    'var(--neutral-20)', // 4: границы
    'var(--neutral-10)', // 5: hover / selected / striped
    'var(--neutral-05)', // 6: заливка инпута и default-варианта, «колодец»
    'var(--neutral-00)', // 7: подложка (body), карточка
    'var(--neutral-00)', // 8
    'var(--neutral-00)', // 9
];

export const palette = {
    primary: ramp8('primary'),
    error: ramp8('error'),
    purple: ramp8('purple'),
    magenta: ramp8('magenta'),
    cyan: ramp8('cyan'),
    teal: ramp8('teal'),
    success: ramp7('success'),
    warning: ramp7('warning'),
    attention: attentionRamp,
    neutral: neutralRamp,
    dark: darkRamp,
} satisfies Record<string, MantineColorsTuple>;
